import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { authGet, authPost } from "../../lib";
import { OrderPanelDialog } from "./Components/OrderPanelDialog";

const useStyles = makeStyles((theme) => ({
  page: { maxWidth: 1200, margin: "24px auto 0", width: "100%" },
  head: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  title: { fontSize: 19, fontWeight: 500 },
  spacer: { flex: 1 },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  panel: { padding: theme.spacing(2) },
  wide: { padding: theme.spacing(2), marginBottom: theme.spacing(2) },
  panelTitle: {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: ".06em",
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1.25),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  editBtn: { padding: 2, color: "rgba(0,0,0,0.4)", "&:hover": { color: "#315179" } },
  line: { fontSize: 13.5, lineHeight: 1.65 },
  muted: { color: theme.palette.text.secondary },
  cell: { fontSize: 13, padding: "6px 10px" },
  num: { fontVariantNumeric: "tabular-nums", textAlign: "right" },
  preTag: {
    marginLeft: 8,
    padding: "1px 5px",
    borderRadius: 3,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: ".04em",
    color: "#4a148c",
    background: "rgba(74,20,140,0.10)",
  },
  warn: { color: "#8a6100", fontSize: 12 },
  itemLink: {
    color: theme.palette.primary.main,
    textDecoration: "none",
    "&:hover": { textDecoration: "underline" },
  },
  addRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(1.5),
    paddingTop: theme.spacing(1.5),
    borderTop: "1px solid rgba(0,0,0,0.08)",
  },
  flags: { display: "flex", gap: theme.spacing(1), flexWrap: "wrap" },
  totals: { marginLeft: "auto", width: 300, marginTop: theme.spacing(1.5) },
  totalRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontSize: 13.5,
    padding: "1px 0",
    minHeight: 24,
  },
  totalValue: { display: "flex", alignItems: "center", gap: 2 },
  totalPad: { paddingRight: 24 },
  qty: { width: 60, "& input": { textAlign: "right", padding: "3px 0" } },
  grand: {
    borderTop: "1px solid rgba(0,0,0,0.12)",
    marginTop: 6,
    paddingTop: 8,
    fontWeight: 700,
    fontSize: 15,
  },
  submitBar: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    padding: theme.spacing(2),
  },
  error: { color: "#c62828", fontSize: 13 },
  hint: { color: theme.palette.text.secondary, fontSize: 12.5 },
}));

const money = (n) =>
  Number(n || 0).toLocaleString(undefined, { style: "currency", currency: "USD" });

/** Which fields each panel owns, so a prefill copies exactly those. */
const BILL_KEYS = [
  "bill_first_name", "bill_mi", "bill_last_name", "bill_business_name",
  "bill_address1", "bill_address2", "bill_city", "bill_state", "bill_zip",
  "bill_country", "special_instructions",
];
const CONTACT_KEYS = ["home_phone", "contact_phone", "email", "email_secondary"];
const SHIP_KEYS = [
  "ship_first_name", "ship_mi", "ship_last_name", "ship_business_name",
  "ship_address1", "ship_address2", "ship_city", "ship_state", "ship_zip",
  "ship_country",
];

/**
 * An account address onto the order's bill_/ship_ fields.
 *
 * account_address stores the parts unprefixed (first_name, address1, …) where
 * the order carries two prefixed copies of the same shape.
 */
const ADDRESS_PARTS = [
  "first_name", "mi", "last_name", "business_name",
  "address1", "address2", "city", "state", "zip", "country",
];
const fromAddress = (addr, prefix) => {
  if (!addr) return {};
  const out = {};
  ADDRESS_PARTS.forEach((k) => {
    if (addr[k]) out[`${prefix}_${k}`] = addr[k];
  });
  return out;
};

const pick = (src, keys) =>
  keys.reduce((acc, k) => {
    if (src?.[k] !== undefined && src[k] !== null) acc[k] = src[k];
    return acc;
  }, {});

/* Items are added by model number alone, as the legacy Add Watch modal did —
   the catalog supplies the description and the price. */

/**
 * Create an order by hand.
 *
 * Two entry points, both landing here:
 *   /orders/new             empty, from the list
 *   /orders/new?from=1234   customer details copied from an existing order
 *
 * Nothing is written until Create — every edit below is in memory, so
 * abandoning the page leaves nothing behind.
 */
export function OrderNewPage() {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  const from = params.get("from");
  const accountId = params.get("account_id");

  const [form, setForm] = useState({ bill_country: "US", ship_country: "US" });
  const [items, setItems] = useState([]);
  const [adding, setAdding] = useState(false);
  const [model, setModel] = useState("");
  const [lookupError, setLookupError] = useState("");
  const [crediting, setCrediting] = useState(false);
  const [credit, setCredit] = useState({ details: "", amount: "" });
  const [sendEmail, setSendEmail] = useState(false);
  const [emailNote, setEmailNote] = useState("");
  const [editing, setEditing] = useState(null);

  const [shipRates, setShipRates] = useState([]);
  const [payMethods, setPayMethods] = useState([]);
  const [loading, setLoading] = useState(!!from || !!accountId);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    authGet("/orders/ship-rates").then(({ data }) => setShipRates(data?.rows ?? []));
    authGet("/orders/lookups").then(({ data }) => setPayMethods(data?.payMethods ?? []));
  }, []);

  /*
   * Prefill copies the customer, not the sale: billing, shipping and contact
   * carry over, while payment, items, gift and totals start empty. That is the
   * point of "New" from an existing order — same buyer, different purchase.
   */
  /*
   * Same idea as copying from an order, but sourced from the account: its
   * default billing and default shipping addresses, and its sign-in email.
   */
  useEffect(() => {
    if (!accountId || from) return;
    let cancelled = false;

    authGet(`/accounts/${accountId}`).then(({ data, error: err }) => {
      if (cancelled) return;
      if (err) {
        setError(err.message || "Could not load that account.");
      } else {
        const addrs = data.addresses ?? [];
        const bill = addrs.find((a) => Number(a.default_bill)) ?? addrs[0] ?? null;
        // Falls back to the billing address, which is what a single-address
        // customer has and what the legacy screen assumed.
        const ship = addrs.find((a) => Number(a.default_ship)) ?? bill;

        setForm((f) => ({
          ...f,
          ...fromAddress(bill, "bill"),
          ...fromAddress(ship, "ship"),
          email: data.account?.email ?? f.email,
          home_phone: bill?.home_phone ?? f.home_phone,
          contact_phone: bill?.contact_phone ?? f.contact_phone,
        }));
      }
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [accountId, from]);

  useEffect(() => {
    if (!from) return;
    let cancelled = false;

    authGet(`/orders/${from}`).then(({ data, error: err }) => {
      if (cancelled) return;
      if (err) setError(err.message || "Could not load that order to copy from.");
      else {
        setForm((f) => ({
          ...f,
          ...pick(data.bill, BILL_KEYS),
          ...pick(data.bill, CONTACT_KEYS),
          ...pick(data.ship, SHIP_KEYS),
          bill_method_id: data.bill?.bill_method_id ?? "",
          is_preowned: data.order?.is_preowned ?? 0,
        }));
      }
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [from]);

  const apply = useCallback((changed) => {
    setForm((f) => ({ ...f, ...changed }));
    setEditing(null);
  }, []);

  /** Resolves the model against the catalog, then appends it at its price. */
  const addItem = async () => {
    const wanted = model.trim();
    if (!wanted) return;
    setLookupError("");

    const { data, error: err } = await authGet(
      `/orders/item-lookup?model_no=${encodeURIComponent(wanted)}`,
    );
    if (err) {
      setLookupError(err.message || "Could not look that model up.");
      return;
    }
    if (!data?.found) {
      setLookupError(
        data?.is_preowned
          ? `No pre-owned item has stock number ${wanted.slice(1)}.`
          : `No catalog item has the model number "${wanted}".`,
      );
      return;
    }

    // The legacy page refuses a pre-owned piece on an order that is not
    // flagged pre-owned — that flag is what keeps the two order books apart,
    // and the server enforces it again on create.
    if (data.is_preowned && !form.is_preowned) {
      setLookupError(
        `${data.label} is pre-owned. Tick "Pre-owned order" at the top before adding it.`,
      );
      return;
    }
    if (data.is_preowned && Number(data.status_id) === 4) {
      setLookupError(`Stock ${data.stock_id} is already marked Sold.`);
      return;
    }

    setItems((list) => [
      ...list,
      {
        key: `${Date.now()}-${list.length}`,
        model_no: data.model_no,
        title: data.title,
        // "Longines Conquest Quartz 41mm L3.759.4.96.6" — what the line shows.
        label: data.label || data.model_no,
        url: data.url,
        price: data.price,
        qty: 1,
        price_on_special: data.price_on_special,
        is_preowned: data.is_preowned ? 1 : 0,
        stock_id: data.stock_id,
        // Anything but In Stock is worth seeing before the order is created.
        status: data.is_preowned && Number(data.status_id) !== 1 ? data.status : null,
      },
    ]);
    setModel("");
    setAdding(false);
  };

  /** A credit or trade-in: free text and an amount that comes off the total. */
  const addCredit = () => {
    const details = credit.details.trim();
    const amount = Math.abs(Number(credit.amount) || 0);
    if (!details || !amount) return;

    setItems((list) => [
      ...list,
      {
        key: `${Date.now()}-${list.length}`,
        is_credit: true,
        details,
        model_no: details,
        title: `Credit ${details}`,
        price: -amount,
        qty: 1,
      },
    ]);
    setCredit({ details: "", amount: "" });
    setCrediting(false);
  };

  const itemsTotal = items.reduce(
    (sum, i) => sum + Number(i.price || 0) * Number(i.qty || 1),
    0,
  );
  const grand =
    itemsTotal +
    Number(form.shipping || 0) +
    Number(form.tax || 0) +
    Number(form.surcharge || 0) -
    Number(form.discount || 0) -
    Number(form.wire_discount || 0);

  const create = async () => {
    setError("");
    if (!items.length) {
      setError("Add at least one item before creating the order.");
      return;
    }
    setBusy(true);

    const body = {
      bill: { ...pick(form, BILL_KEYS), ...pick(form, CONTACT_KEYS), bill_method_id: form.bill_method_id },
      ship: {
        ...pick(form, SHIP_KEYS),
        ship_rate_id: form.ship_rate_id,
        gift_wrap: form.gift_wrap ? 1 : 0,
        gift_message: form.gift_message,
      },
      order: {
        shipping: form.shipping,
        tax: form.tax,
        discount: form.discount,
        wire_discount: form.wire_discount,
        surcharge: form.surcharge,
        is_preowned: form.is_preowned ? 1 : 0,
        previous_customer: form.previous_customer ? 1 : 0,
        return_end: form.return_end,
        sizing_instructions: form.sizing_instructions,
        notes_customer: form.notes_customer,
      },
      items: items.map(({ key, label, url, stock_id, status, ...i }) => i),
    };

    const { data, error: err } = await authPost("/orders/create", body);

    if (err) {
      setBusy(false);
      setError(err.message || "Could not create the order.");
      return;
    }

    /*
     * The confirmation goes out through the same guarded action endpoints as
     * a resend: composed server-side, and refused wherever sending is off. So
     * ticking this on a development machine creates the order and tells you
     * the email was not sent, rather than quietly mailing a customer.
     */
    if (sendEmail) {
      const { data: preview } = await authGet(
        `/orders/${data.order_id}/actions/resend`,
      );
      if (preview?.sending?.blocked) {
        setBusy(false);
        setEmailNote(`Order created. Email not sent: ${preview.sending.blocked}`);
        return;
      }
      const { data: sent } = await authPost(
        `/orders/${data.order_id}/actions/resend`,
        { to: preview?.to, subject: preview?.subject, body: preview?.body },
      );
      if (sent?.sent === false) {
        setBusy(false);
        setEmailNote(`Order created. Email not sent: ${sent.blocked}`);
        return;
      }
    }

    history.push(`/orders/${data.order_id}`);
  };

  if (loading) {
    return (
      <Box className={classes.page} textAlign="center" pt={6}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  const card = (title, panel, body) => (
    <Paper className={classes.panel}>
      <div className={classes.panelTitle}>
        <span>{title}</span>
        <IconButton size="small" className={classes.editBtn} onClick={() => setEditing(panel)}>
          <EditIcon style={{ fontSize: 15 }} />
        </IconButton>
      </div>
      <div className={classes.line}>{body}</div>
    </Paper>
  );

  const addressLines = (prefix) => {
    const name = [form[`${prefix}_first_name`], form[`${prefix}_mi`], form[`${prefix}_last_name`]]
      .filter(Boolean)
      .join(" ");
    const city = [form[`${prefix}_city`], form[`${prefix}_state`]].filter(Boolean).join(", ");
    const lines = [
      name,
      form[`${prefix}_business_name`],
      form[`${prefix}_address1`],
      form[`${prefix}_address2`],
      [city, form[`${prefix}_zip`]].filter(Boolean).join(" "),
      form[`${prefix}_country`],
    ].filter((l) => l && String(l).trim());

    return lines.length ? (
      lines.map((l, n) => <div key={n}>{l}</div>)
    ) : (
      <span className={classes.muted}>Not set</span>
    );
  };

  return (
    <Box className={classes.page}>
      <div className={classes.head}>
        <Button size="small" variant="outlined" onClick={() => history.push("/orders")}>
          ← Orders
        </Button>
        <Typography className={classes.title}>New Order</Typography>
        <div className={classes.spacer} />
        <FormControlLabel
          control={
            <Checkbox
              color="primary"
              checked={!!form.is_preowned}
              onChange={(e) => setForm((f) => ({ ...f, is_preowned: e.target.checked ? 1 : 0 }))}
            />
          }
          label="Pre-owned order"
        />
      </div>

      <div className={classes.grid}>
        {card("Billing", "billing", addressLines("bill"))}
        {card("Shipping", "shipping", addressLines("ship"))}
        {card(
          "Contact",
          "contact",
          <>
            <div>{form.email || <span className={classes.muted}>No email</span>}</div>
            {form.email_secondary && <div>{form.email_secondary}</div>}
            <div>{form.home_phone || <span className={classes.muted}>No phone</span>}</div>
            {form.contact_phone && <div>{form.contact_phone}</div>}
          </>,
        )}
      </div>

      <Paper className={classes.wide}>
        <div className={classes.panelTitle}>
          <span>Payment</span>
        </div>
        <div className={classes.addRow} style={{ marginTop: 0, paddingTop: 0, border: 0 }}>
          <TextField
            select
            label="Payment method"
            style={{ width: 220 }}
            value={form.bill_method_id ?? ""}
            onChange={(e) => setForm((f) => ({ ...f, bill_method_id: e.target.value }))}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {payMethods.map((p) => (
              <MenuItem key={p.pay_method_id} value={p.pay_method_id}>
                {p.pay_method}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            type="date"
            label="Return expires"
            style={{ width: 180 }}
            value={form.return_end ?? ""}
            onChange={(e) => setForm((f) => ({ ...f, return_end: e.target.value }))}
          />
        </div>
      </Paper>

      <Paper className={classes.wide}>
        <div className={classes.panelTitle}>
          <span>Gift &amp; Instructions</span>
          <IconButton size="small" className={classes.editBtn} onClick={() => setEditing("gift")}>
            <EditIcon style={{ fontSize: 15 }} />
          </IconButton>
        </div>
        <div className={classes.line}>
          <div>Gift wrap: {form.gift_wrap ? "Yes" : "No"}</div>
          {form.gift_message && <div>{form.gift_message}</div>}
          {form.special_instructions && <div>{form.special_instructions}</div>}
          {form.sizing_instructions && <div>{form.sizing_instructions}</div>}
        </div>
      </Paper>

      <Paper className={classes.wide}>
        <div className={classes.panelTitle}>
          <span>Items</span>
        </div>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell className={classes.cell}>Item</TableCell>
              <TableCell className={`${classes.cell} ${classes.num}`} style={{ width: 90 }}>
                Qty
              </TableCell>
              <TableCell className={`${classes.cell} ${classes.num}`} style={{ width: 130 }}>
                Price
              </TableCell>
              <TableCell className={`${classes.cell} ${classes.num}`} style={{ width: 130 }}>
                Line total
              </TableCell>
              <TableCell className={classes.cell} style={{ width: 44 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((i, n) => (
              <TableRow key={i.key}>
                <TableCell className={classes.cell}>
                  {i.is_credit ? (
                    i.title
                  ) : i.url ? (
                    <a
                      className={classes.itemLink}
                      href={i.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {i.label ?? i.model_no}
                    </a>
                  ) : (
                    i.label ?? i.model_no
                  )}
                  {!!i.is_preowned && (
                    <span className={classes.preTag} title={`Stock ${i.stock_id}`}>
                      PRE-OWNED
                    </span>
                  )}
                  {i.status && <span className={classes.warn}> · {i.status}</span>}
                </TableCell>
                <TableCell className={`${classes.cell} ${classes.num}`}>
                  {i.is_credit ? (
                    1
                  ) : (
                    <TextField
                      type="number"
                      className={classes.qty}
                      value={i.qty}
                      onChange={(e) =>
                        setItems((list) =>
                          list.map((row, x) =>
                            x === n
                              ? { ...row, qty: Math.max(1, Number(e.target.value) || 1) }
                              : row,
                          ),
                        )
                      }
                    />
                  )}
                </TableCell>
                {/* Price comes from the catalog and is not typed here. */}
                <TableCell className={`${classes.cell} ${classes.num}`}>
                  {money(i.price)}
                </TableCell>
                <TableCell className={`${classes.cell} ${classes.num}`}>
                  {money(Number(i.price) * Number(i.qty || 1))}
                </TableCell>
                <TableCell className={classes.cell}>
                  <IconButton
                    size="small"
                    className={classes.editBtn}
                    title="Remove"
                    onClick={() => setItems((list) => list.filter((_, x) => x !== n))}
                  >
                    <DeleteOutlineIcon style={{ fontSize: 16 }} />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {/* Shipping is a line on the order, not a field on the payment
                card — same as the legacy items table. */}
            {(form.ship_rate_id || Number(form.shipping)) > 0 && (
              <TableRow>
                <TableCell className={classes.cell}>
                  {shipRates.find((r) => String(r.ship_rate_id) === String(form.ship_rate_id))
                    ?.description ?? "Shipping"}
                </TableCell>
                <TableCell className={`${classes.cell} ${classes.num}`}>1</TableCell>
                <TableCell className={`${classes.cell} ${classes.num}`}>
                  {money(form.shipping)}
                </TableCell>
                <TableCell className={`${classes.cell} ${classes.num}`}>
                  {money(form.shipping)}
                </TableCell>
                <TableCell className={classes.cell}>
                  <IconButton
                    size="small"
                    className={classes.editBtn}
                    title="Edit shipping"
                    onClick={() => setEditing("method")}
                  >
                    <EditIcon style={{ fontSize: 15 }} />
                  </IconButton>
                </TableCell>
              </TableRow>
            )}
            {!items.length && (
              <TableRow>
                <TableCell className={classes.cell} colSpan={5}>
                  <span className={classes.muted}>No items yet.</span>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <div className={classes.addRow}>
          <Button size="small" variant="outlined" onClick={() => setAdding(true)}>
            + Add item
          </Button>
          <Button size="small" variant="outlined" onClick={() => setCrediting(true)}>
            − Credit / Trade-In
          </Button>
          {lookupError && <span className={classes.error}>{lookupError}</span>}
        </div>

        <div className={classes.totals}>
          <div className={classes.totalRow}>
            <span>Subtotal</span>
            <span className={classes.totalValue}>
              {money(itemsTotal)}
              <IconButton
                size="small"
                className={classes.editBtn}
                title="Edit price"
                onClick={() => setEditing("adjustments")}
              >
                <EditIcon style={{ fontSize: 14 }} />
              </IconButton>
            </span>
          </div>
          <div className={classes.totalRow}>
            <span>Shipping</span>
            <span className={classes.totalValue}>
              {money(form.shipping)}
              <IconButton
                size="small"
                className={classes.editBtn}
                title="Edit shipping"
                onClick={() => setEditing("method")}
              >
                <EditIcon style={{ fontSize: 14 }} />
              </IconButton>
            </span>
          </div>
          <div className={classes.totalRow}>
            <span>Tax</span>
            <span className={classes.totalPad}>{money(form.tax)}</span>
          </div>
          {Number(form.discount) > 0 && (
            <div className={classes.totalRow}>
              <span>Discount</span>
              <span className={classes.totalPad}>−{money(form.discount)}</span>
            </div>
          )}
          {Number(form.wire_discount) > 0 && (
            <div className={classes.totalRow}>
              <span>Wire discount</span>
              <span className={classes.totalPad}>−{money(form.wire_discount)}</span>
            </div>
          )}
          {Number(form.surcharge) > 0 && (
            <div className={classes.totalRow}>
              <span>Surcharge</span>
              <span className={classes.totalPad}>{money(form.surcharge)}</span>
            </div>
          )}
          <div className={`${classes.totalRow} ${classes.grand}`}>
            <span>Total</span>
            <span className={classes.totalPad}>{money(grand)}</span>
          </div>
        </div>
      </Paper>

      <Paper className={classes.wide}>
        <div className={classes.submitBar}>
          {error && <span className={classes.error}>{error}</span>}
          {emailNote && <span className={classes.hint}>{emailNote}</span>}
          {/* Everything right-aligned, with Create last. */}
          <div className={classes.spacer} />
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
              />
            }
            label="Send confirmation email"
          />
          <Button onClick={() => history.push("/orders")} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={busy || !items.length}
            onClick={create}
          >
            {busy ? "Creating…" : "Create Order"}
          </Button>
        </div>
      </Paper>

      {/* Add Watch: a model number and nothing else, as the legacy modal had
          it. The catalog supplies the description and the price. */}
      <Dialog open={adding} onClose={() => setAdding(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Watch</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Model Number"
            helperText={
              form.is_preowned
                ? "Pre-owned: enter P followed by the stock number, e.g. P68106"
                : undefined
            }
            value={model}
            onChange={(e) => setModel(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addItem()}
          />
          {lookupError && (
            <div className={classes.error} style={{ marginTop: 10 }}>
              {lookupError}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setAdding(false)}>
            Close
          </Button>
          <Button
            size="small"
            variant="contained"
            color="primary"
            disabled={!model.trim()}
            onClick={addItem}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={crediting} onClose={() => setCrediting(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Refund / Trade-In</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Details"
            placeholder="Trade 7505 - 9028180"
            value={credit.details}
            onChange={(e) => setCredit((c) => ({ ...c, details: e.target.value }))}
          />
          <Box mt={2}>
            <TextField
              fullWidth
              type="number"
              label="Amount"
              helperText="Comes off the order total"
              value={credit.amount}
              onChange={(e) => setCredit((c) => ({ ...c, amount: e.target.value }))}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setCrediting(false)}>
            Close
          </Button>
          <Button
            size="small"
            variant="contained"
            color="primary"
            disabled={!credit.details.trim() || !Number(credit.amount)}
            onClick={addCredit}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {editing && (
        <OrderPanelDialog
          panel={editing}
          values={form}
          shipRates={shipRates}
          onClose={() => setEditing(null)}
          onApply={apply}
        />
      )}
    </Box>
  );
}

export default OrderNewPage;
